import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, API_BASE, AED_short, getSessionToken, getWorkspace } from '../lib/api';
import { toast } from 'sonner';
import {
  Database, Filter, X, Download, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, Search, Loader2, Sliders, Eye, EyeOff, RotateCcw,
} from 'lucide-react';

// ─── Column catalog ────────────────────────────────────────────────────
const ALL_COLS = [
  { key: 'transaction_date', label: 'Date',        sortable: true,  fmt: (v) => v || '—' },
  { key: 'community',        label: 'Community',   sortable: true,  fmt: (v) => v || '—' },
  { key: 'building',         label: 'Building',    sortable: true,  fmt: (v) => v || '—' },
  { key: 'transaction_type', label: 'Type',        sortable: false,
    fmt: (v) => v
      ? <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${v==='rent'?'bg-amber-100 text-amber-800':'bg-emerald-100 text-emerald-800'}`}>{v}</span>
      : '—' },
  { key: 'bedrooms',         label: 'Beds',        sortable: true,
    fmt: (v) => v == null ? '—' : (v === 0 ? 'Studio' : v) },
  { key: 'size_sqft',        label: 'Size sqft',   sortable: true,
    fmt: (v) => v ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—' },
  { key: 'price_aed',        label: 'Price',       sortable: true,  fmt: (v) => AED_short(v) },
  { key: 'aed_per_sqft',     label: 'AED / sqft',  sortable: true,
    fmt: (v) => v ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—' },
  { key: 'is_off_plan',      label: 'Off-plan',    sortable: false,
    fmt: (v) => v === true ? <span className="text-fuchsia-700 font-medium">Off-plan</span>
             : v === false ? <span className="text-slate-500">Ready</span>
             : <span className="text-slate-300">—</span> },
  { key: 'property_type',    label: 'Property',    sortable: false, fmt: (v) => v || '—', optional: true },
  { key: 'source',           label: 'Source',      sortable: false, fmt: (v) => v || '—', optional: true },
  { key: 'procedure_name',   label: 'Procedure',   sortable: false, fmt: (v) => v || '—', optional: true },
  { key: 'external_id',      label: 'External ID', sortable: false, fmt: (v) => <span className="font-mono text-[10px] text-slate-500">{v || '—'}</span>, optional: true },
];

const DEFAULT_VISIBLE = new Set([
  'transaction_date','community','building','transaction_type',
  'bedrooms','size_sqft','price_aed','aed_per_sqft','is_off_plan',
]);

const EMPTY_FILTERS = {
  date_from: '', date_to: '',
  communities: [], transaction_type: '', is_off_plan: '',
  min_price: '', max_price: '',
  min_size_sqft: '', max_size_sqft: '',
  min_aed_per_sqft: '', max_aed_per_sqft: '',
  bedrooms: [], property_type: '', source: '', q: '',
};

// ─── Small helpers ─────────────────────────────────────────────────────
function serialize(filters) {
  const p = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return;
    p[k] = Array.isArray(v) ? v.join(',') : v;
  });
  return p;
}

// ─── Community multi-select popover ────────────────────────────────────
function CommunityPicker({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(
    () => options.filter(o => o.name.toLowerCase().includes(q.toLowerCase())).slice(0, 100),
    [options, q]
  );
  const toggle = (name) =>
    onChange(selected.includes(name) ? selected.filter(s => s !== name) : [...selected, name]);
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="community-picker-toggle"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm hover:border-slate-300 flex items-center justify-between gap-2"
      >
        <span className="truncate">
          {selected.length === 0 ? <span className="text-slate-400">Any community</span>
            : selected.length === 1 ? selected[0]
            : `${selected.length} selected`}
        </span>
        <Filter size={14} className="text-slate-400 shrink-0"/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute z-50 mt-1 w-[360px] max-h-[420px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
            <div className="p-2 border-b border-slate-100 flex items-center gap-2">
              <Search size={14} className="text-slate-400"/>
              <input
                data-testid="community-search-input"
                autoFocus
                placeholder="Search communities…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="flex-1 text-sm outline-none"
              />
              {selected.length > 0 && (
                <button
                  onClick={() => onChange([])}
                  className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
                >
                  <X size={12}/> clear
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[360px]">
              {filtered.length === 0 && (
                <div className="p-3 text-sm text-slate-400">No matches.</div>
              )}
              {filtered.map(o => {
                const checked = selected.includes(o.name);
                return (
                  <label
                    key={o.name}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer ${checked ? 'bg-emerald-50' : ''}`}
                  >
                    <input
                      type="checkbox" checked={checked}
                      onChange={() => toggle(o.name)}
                      className="accent-[#679286]"
                    />
                    <span className="flex-1 truncate">{o.name}</span>
                    <span className="text-[10px] text-slate-400 tabular-nums">{o.count.toLocaleString()}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const active = sortBy === col.key;
  return (
    <button
      onClick={() => col.sortable && onSort(col.key)}
      className={`inline-flex items-center gap-1 ${col.sortable ? 'hover:text-slate-900 cursor-pointer' : 'cursor-default'} ${active ? 'text-slate-900' : 'text-slate-500'}`}
      data-testid={`sort-${col.key}`}
    >
      {col.label}
      {col.sortable && active && (sortDir === 'desc' ? <ArrowDown size={12}/> : <ArrowUp size={12}/>)}
    </button>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────
export default function TransactionsBrowser() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('transaction_date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [visible, setVisible] = useState(DEFAULT_VISIBLE);
  const [showColsMenu, setShowColsMenu] = useState(false);
  const seq = useRef(0);

  // Load facets once
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/transactions/facets');
        setFacets(r.data);
      } catch (e) {
        toast.error('Failed to load filter options: ' + (e?.response?.data?.detail || e.message));
      }
    })();
  }, []);

  // Load rows whenever filters/sort/page change
  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    setLoading(true);
    try {
      const params = {
        ...serialize(filters),
        sort_by: sortBy, sort_dir: sortDir,
        page, page_size: pageSize,
        include_summary: true,
      };
      const r = await api.get('/transactions/browse', { params });
      if (mySeq !== seq.current) return; // stale response — drop
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || null);
    } catch (e) {
      if (mySeq === seq.current) {
        toast.error('Failed to load transactions: ' + (e?.response?.data?.detail || e.message));
      }
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [filters, sortBy, sortDir, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const patch = (part) => { setFilters(f => ({ ...f, ...part })); setPage(1); };
  const reset = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  const onSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(key); setSortDir('desc'); }
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    Object.entries(filters).forEach(([, v]) => {
      if (Array.isArray(v)) { if (v.length) n++; }
      else if (v) n++;
    });
    return n;
  }, [filters]);

  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const p = new URLSearchParams({
        ...serialize(filters), sort_by: sortBy, sort_dir: sortDir, limit: '200000',
      });
      const token = getSessionToken();
      const ws = getWorkspace();
      const url = `${API_BASE}/transactions/export.csv?${p.toString()}`;
      const res = await fetch(url, {
        credentials: 'include',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'X-Workspace': ws,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `iris_transactions_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const cols = ALL_COLS.filter(c => visible.has(c.key));
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="transactions-browser-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#679286]/10 grid place-items-center">
            <Database size={20} className="text-[#679286]"/>
          </div>
          <div>
            <h1 className="font-serif text-3xl">Transactions Browser</h1>
            <p className="text-sm text-slate-500">
              Full DLD transaction archive · filter, sort, export ·
              {' '}
              {facets ? <span className="tabular-nums">{facets.total_rows.toLocaleString()} rows total</span> : 'loading…'}
              {facets?.date_range?.oldest && facets?.date_range?.newest && (
                <> · {facets.date_range.oldest} → {facets.date_range.newest}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reset-filters-btn"
            onClick={reset}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            <RotateCcw size={14}/> Reset
          </button>
          <div className="relative">
            <button
              data-testid="cols-menu-toggle"
              onClick={() => setShowColsMenu(v => !v)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
            >
              <Sliders size={14}/> Columns
            </button>
            {showColsMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowColsMenu(false)}/>
                <div className="absolute right-0 z-50 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                  {ALL_COLS.map(c => {
                    const on = visible.has(c.key);
                    return (
                      <button
                        key={c.key}
                        onClick={() => {
                          const next = new Set(visible);
                          if (on) next.delete(c.key); else next.add(c.key);
                          setVisible(next);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 text-left"
                      >
                        {on ? <Eye size={14} className="text-emerald-600"/>
                            : <EyeOff size={14} className="text-slate-300"/>}
                        <span className={on ? '' : 'text-slate-400'}>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={doExport}
            disabled={exporting || total === 0}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-[#679286] text-white hover:bg-[#557568] disabled:opacity-50"
          >
            {exporting ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter grid */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Filter size={14}/> Filters
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* Date */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">From</label>
            <input
              data-testid="filter-date-from"
              type="date" value={filters.date_from}
              onChange={(e) => patch({ date_from: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">To</label>
            <input
              data-testid="filter-date-to"
              type="date" value={filters.date_to}
              onChange={(e) => patch({ date_to: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          {/* Community */}
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Community</label>
            <div className="mt-1">
              <CommunityPicker
                options={facets?.communities || []}
                selected={filters.communities}
                onChange={(v) => patch({ communities: v })}
              />
            </div>
          </div>
          {/* Type / off-plan */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Transaction type</label>
            <select
              data-testid="filter-transaction-type"
              value={filters.transaction_type}
              onChange={(e) => patch({ transaction_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="">Any</option>
              <option value="sale">Sale</option>
              <option value="rent">Rent</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Off-plan</label>
            <select
              data-testid="filter-off-plan"
              value={filters.is_off_plan}
              onChange={(e) => patch({ is_off_plan: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="">Any</option>
              <option value="true">Off-plan only</option>
              <option value="false">Ready only</option>
            </select>
          </div>
          {/* Bedrooms */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Bedrooms</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {['0','1','2','3','4','5','6','7'].map(b => {
                const on = filters.bedrooms.includes(Number(b));
                return (
                  <button
                    key={b}
                    data-testid={`bed-btn-${b}`}
                    onClick={() => {
                      const bi = Number(b);
                      const next = on ? filters.bedrooms.filter(x => x !== bi) : [...filters.bedrooms, bi];
                      patch({ bedrooms: next });
                    }}
                    className={`px-2.5 py-1 text-xs rounded-lg border ${on ? 'bg-[#679286] text-white border-[#679286]' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                  >
                    {b === '0' ? 'Studio' : `${b}${b === '7' ? '+' : ''}`}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Source */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Source</label>
            <select
              data-testid="filter-source"
              value={filters.source}
              onChange={(e) => patch({ source: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="">Any</option>
              {(facets?.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* Free text */}
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Search (community / building)</label>
            <input
              data-testid="filter-q"
              value={filters.q}
              onChange={(e) => patch({ q: e.target.value })}
              placeholder="e.g. Marina Gate"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          {/* Ranges — compact 2-column pairs */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Price min (AED)</label>
            <input
              data-testid="filter-min-price" type="number"
              value={filters.min_price}
              onChange={(e) => patch({ min_price: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Price max (AED)</label>
            <input
              data-testid="filter-max-price" type="number"
              value={filters.max_price}
              onChange={(e) => patch({ max_price: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="∞"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Size sqft min</label>
            <input
              data-testid="filter-min-size" type="number"
              value={filters.min_size_sqft}
              onChange={(e) => patch({ min_size_sqft: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Size sqft max</label>
            <input
              data-testid="filter-max-size" type="number"
              value={filters.max_size_sqft}
              onChange={(e) => patch({ max_size_sqft: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="∞"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">AED/sqft min</label>
            <input
              data-testid="filter-min-psf" type="number"
              value={filters.min_aed_per_sqft}
              onChange={(e) => patch({ min_aed_per_sqft: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">AED/sqft max</label>
            <input
              data-testid="filter-max-psf" type="number"
              value={filters.max_aed_per_sqft}
              onChange={(e) => patch({ max_aed_per_sqft: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="∞"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-slate-500">Property type</label>
            <select
              data-testid="filter-property-type"
              value={filters.property_type}
              onChange={(e) => patch({ property_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="">Any</option>
              {(facets?.property_types || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SummaryCard label="Rows matched" value={total.toLocaleString()} loading={loading}/>
        <SummaryCard label="Median price"
          value={summary?.median_price != null ? AED_short(summary.median_price) : '—'} loading={loading}/>
        <SummaryCard label="Median AED/sqft"
          value={summary?.median_aed_per_sqft != null ? Math.round(summary.median_aed_per_sqft).toLocaleString() : '—'} loading={loading}/>
        <SummaryCard label="Date span"
          value={summary?.date_min && summary?.date_max ? `${summary.date_min} → ${summary.date_max}` : '—'} loading={loading}/>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transactions-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {cols.map(c => (
                  <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap">
                    <SortHeader col={c} sortBy={sortBy} sortDir={sortDir} onSort={onSort}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={cols.length} className="px-3 py-12 text-center text-slate-400">
                  <Loader2 size={22} className="animate-spin inline mr-2"/> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={cols.length} className="px-3 py-12 text-center text-slate-400">
                  No transactions match the current filters.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.external_id || i} className="border-t border-slate-100 hover:bg-slate-50/70">
                  {cols.map(c => (
                    <td key={c.key} className="px-3 py-2 whitespace-nowrap tabular-nums">
                      {c.fmt(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="px-2 py-1 rounded border border-slate-200 bg-white"
              data-testid="page-size-select"
            >
              {[25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-slate-400">·</span>
            <span className="tabular-nums">
              {total === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="page-prev"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft size={14}/>
            </button>
            <span className="px-2 tabular-nums">Page {page} / {pageCount}</span>
            <button
              data-testid="page-next"
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronRight size={14}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, loading }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className={`text-lg font-serif truncate ${loading ? 'opacity-40' : ''}`}>{value}</div>
    </div>
  );
}
